import React, { useRef, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import FacebookIcon from "@mui/icons-material/Facebook";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import { getHome } from "../services/api/homeApi";
import { useNavigate } from "react-router-dom";
export default function ViralSection() {
  const theme = useTheme();
const navigate = useNavigate();
  const [viralText, setViralText] = useState([]);
  const [viralImages, setViralImages] = useState([]);
  const [textIndex, setTextIndex] = useState(0);

  const textRef = useRef(null);
  const sliderRef = useRef(null);
  const autoScrollRef = useRef(null);

  const CARD_WIDTH = 300;

  /* ================= FETCH API ================= */

  useEffect(() => {
    const fetchHome = async () => {
      try {
        const data = await getHome();
        setViralText(data?.viral?.text || []);
        setViralImages(data?.viral?.image || []);
      } catch (error) {
        console.error("Failed to load viral section:", error);
      }
    };

    fetchHome();
  }, []);

  /* ================= ICON DETECTION ================= */

  const getIconFromLink = (link) => {
    if (!link) return null;

    if (link.includes("instagram"))
      return <InstagramIcon />;

    if (link.includes("youtube"))
      return <YouTubeIcon />;

    if (link.includes("facebook"))
      return <FacebookIcon />;

    if (link.includes("tiktok"))
      return <MusicNoteIcon />;

    return null;
  };

  /* ================= TEXT AUTO SLIDER ================= */

  useEffect(() => {
    if (viralText.length === 0) return;

    const timer = setInterval(() => {
      setTextIndex((prev) => (prev + 1) % viralText.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [viralText]);

  /* ================= IMAGE INFINITE SCROLL ================= */

  const repeatedCards =
    viralImages.length > 0
      ? [...viralImages, ...viralImages, ...viralImages]
      : [];

  useEffect(() => {
    if (sliderRef.current && repeatedCards.length > 0) {
      sliderRef.current.scrollLeft =
        sliderRef.current.scrollWidth / 3;
    }
  }, [repeatedCards.length]);

  const SCROLL_SPEED = 0.5;
  const SCROLL_INTERVAL = 20;

  const startAutoScroll = () => {
    autoScrollRef.current = setInterval(() => {
      if (!sliderRef.current) return;
      sliderRef.current.scrollLeft += SCROLL_SPEED;
      handleInfiniteScroll();
    }, SCROLL_INTERVAL);
  };

  const stopAutoScroll = () => {
    clearInterval(autoScrollRef.current);
  };

  useEffect(() => {
    if (repeatedCards.length > 0) startAutoScroll();
    return () => stopAutoScroll();
  }, [repeatedCards.length]);

  const handleInfiniteScroll = () => {
    const slider = sliderRef.current;
    if (!slider) return;

    const oneSet = slider.scrollWidth / 3;

    if (slider.scrollLeft <= oneSet * 0.5) {
      slider.scrollLeft += oneSet;
    } else if (slider.scrollLeft >= oneSet * 1.5) {
      slider.scrollLeft -= oneSet;
    }
  };

  const handleMouseDown = (e) => {
    stopAutoScroll();
    const slider = sliderRef.current;
    slider.isDown = true;
    slider.startX = e.pageX;
    slider.scrollStart = slider.scrollLeft;
  };

  const stopDrag = () => {
    startAutoScroll();
    if (sliderRef.current) sliderRef.current.isDown = false;
  };

  const handleMouseMove = (e) => {
    const slider = sliderRef.current;
    if (!slider?.isDown) return;

    slider.scrollLeft =
      slider.scrollStart - (e.pageX - slider.startX);
    handleInfiniteScroll();
  };

  const scrollByArrow = (dir) => {
    stopAutoScroll();
    sliderRef.current.scrollBy({
      left: dir === "left" ? -CARD_WIDTH : CARD_WIDTH,
      behavior: "smooth",
    });
    setTimeout(startAutoScroll, 1500);
  };

  /* ================= RENDER ================= */

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: "1440px",
          mx: "auto",
          px: { xs: 3, md: 0.7 }, 
        }}
      >
        {/* ================= TEXT SLIDER ================= */}
        <Box sx={{ position: "relative", overflow: "hidden", pb: 4 }}>
          <Box
            ref={textRef}
            sx={{
              display: "flex",
              transform: `translateX(-${textIndex * 100}%)`,
              transition: "transform .6s ease",
            }}
          >
            {viralText.map((item, i) => (
              <Box
                key={i}
                sx={{
                  minWidth: "100%",
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  alignItems: { md: "flex" }, 
                  justifyContent: "space-between",
                  gap: { xs: 2, md: 6 }, 
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: 26, md: 48 },
                    fontWeight: 800,
                    ml:{xs:0, md:2},
                    maxWidth: { xs: "100%", md: "55%" },
                  }}
                >
                  {item.title}
                </Typography>

                <Box maxWidth={420}>
                  <Typography mb={3}>{item.text}</Typography>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    {/* <Button variant="contained">
                      Skin Care Hub
                    </Button> */}
                    <Button variant="outlined"  onClick={() => navigate("/advice-care")}>
                      Advice & Care
                    </Button>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Box>

          {/* DOTS */}
          <Stack direction="row" justifyContent="center" spacing={1} mt={2}>
            {viralText.map((_, i) => (
              <Box
                key={i}
                onClick={() => setTextIndex(i)}
                sx={{
                  width: textIndex === i ? 22 : 8,
                  height: 8,
                  borderRadius: 10,
                  transition: "0.3s",
                  bgcolor:
                    textIndex === i
                      ? "primary.main"
                      : "grey.400",
                  cursor: "pointer",
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* ================= IMAGE SLIDER ================= */}
        <Box
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          onMouseMove={handleMouseMove}
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            px: { xs: 0, md: 0 }, // ✅ no extra side gap
            "&::-webkit-scrollbar": { display: "none" },
            cursor: "grab",
          }}
        >
          {repeatedCards.map((card, i) => (
            <Box
              key={i}
              sx={{
                width: CARD_WIDTH,
                height: 360,
                flexShrink: 0,
                borderRadius: 6,
                overflow: "hidden",
                position: "relative",
              }}
            >
              <Box
                component="img"
                src={card.image}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="a"
                  href={card.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: "50%",
                    background:
                      theme.custom?.colors?.overlay?.whiteLight ||
                      "rgba(255,255,255,0.6)",
                    backdropFilter: "blur(10px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "& svg": {
                      fontSize: 42,
                      color: "primary.contrastText",
                    },
                  }}
                >
                  {getIconFromLink(card.link)}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        {/* MOBILE ARROWS */}
        <Stack direction="row" spacing={2} mt={3}>
          <IconButton onClick={() => scrollByArrow("left")}>
            <ArrowBackIcon />
          </IconButton>
          <IconButton onClick={() => scrollByArrow("right")}>
            <ArrowForwardIcon />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );
}
